import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { BRAND_NAME } from "@/utils/constants";
import { selectProduct } from "@/store/slices/product";
import { useSelector } from "react-redux";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import styles from "./slider.module.scss";
import { GA4_ITEM_BRAND } from "@/lib/ga4";
import { useWindowSize } from "usehooks-ts";
import SwiperCore, { FreeMode, Navigation, Thumbs } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import Image from "next/image";
import InnerImageZoom from "react-inner-image-zoom";
import { initTryOnEventListener } from "@/lib/tryOnTracking";
import endpoints from "@/api/endpoints";
import httpClient from "@/api/http-client";
import { authActions, selectAuth } from "@/store/slices/auth";
import { useAppDispatch } from "@/store";
import { fetchCartItems, selectCart } from "@/store/slices/cart";
import { toast } from "react-toastify";
import { fbqTrackEvent } from "@/lib/fpixel";
import { ptrackEvent } from "@/lib/ppixel";
import { syteTrackEvent } from "@/lib/syte";
import { S3ImageLoader } from "@/image-loaders/s3-image-loader";
import SliderImage from "@/components/react-zoom-slide/SliderImage";
import { unbxdCusEngTrackEvent } from "@/lib/unbxd";
import SimilarProduct from "@/components/similar-product/similar-product";
import LoadingSpinner from "@/components/common/loading-spinner";
import LoadingSpinnerSmall from "@/components/common/loading-spinner-small";
import { Spinner } from "react-bootstrap";

const ProductDetailsSlider = () => {
  SwiperCore.use([Navigation, FreeMode, Thumbs]);
  const productState = useSelector(selectProduct);
  const { width } = useWindowSize();
  const [thumbsSwiper, setThumbsSwiper] = useState<any>(null);
  const dispatch = useAppDispatch();
  const authState = useSelector(selectAuth);
  const cartState = useSelector(selectCart);
  const [tryOnAddToCart, setTryOnAddToCart] = useState(false);
  const [tryOnRemoveFromCart, setTryOnRemoveFromCart] = useState(false);
  const [videoLoading, setVideoLoading] = useState(true);
  const videoRefs = useRef<Record<number, HTMLVideoElement | null>>({});
  const [show, setShow] = useState(false);
  const [skus, setskus] = useState<any>([]);
  const [similarLoading, setSimilarLoading] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = async (image: any) => {
    setSimilarLoading(true);
    const formdata = new FormData();
    formdata.append("image_url", image);
    formdata.append("tags", process.env.NEXT_PUBLIC_SIMILAR_API_TAG || "");
    try {
      const { data } = await httpClient.post(
        endpoints.products.similarproduct(),
        formdata
      );
      const filteredImages = data.images.filter((v: any) => v.distance > 0.65);
      await setskus(filteredImages.map((v: any) => v.product_id));
      setShow(true);
    } catch (error: any) {
      toast(error.response.data?.message || "No products found", {
        type: "error",
        toastId: "coupon-otp-error",
      });
    } finally {
      setSimilarLoading(false);
    }
  };
  const [isOpen, setIsOpen] = useState(false);

  const cartItem = useMemo(
    () =>
      cartState.items.find(
        (v) => v.identifier == productState.selectedPrice?.id?.toString() || ""
      ) || null,
    [cartState.items, productState.selectedPrice?.id]
  );

  const addToCart = async () => {
    if (!authState.user) {
      dispatch(authActions.setIsModalOpen(true));
      return;
    }
    await httpClient.post(endpoints.cart.add(), {
      is_solitaire: false,
      type: "product",
      identifier: productState.selectedPrice?.id,
    });
    if (window.Moengage) {
      window.Moengage.track_event("Added to Cart", {
        name: productState.data?.title,
        id: productState.data?.sku,
        amount: productState.selectedPrice?.discount
          ? productState.selectedPrice?.discount.total
          : productState.selectedPrice?.total,
      });
    }
      unbxdCusEngTrackEvent("Add to cart ", {
        source: "web",
        platform: "senco",
        prid: productState.data?.id,
        variant_id: productState.selectedPrice?.id,
        image_url: productState.data?.images[0].media,
        prqt: 1,
        product_name: productState.data?.title,
        category: productState.data?.categories?.length
          ? [...productState.data.categories].reverse()[0]?.name
          : "",
        brand: BRAND_NAME,
        amount: productState.selectedPrice?.total,
        product_url: `${window.location.origin}/jewellery/${productState.data?.slug}`,
        discounted_price: productState.selectedPrice?.discount?.price || 0,
        stock_availability: productState.selectedPrice?.stock ? 1 : 0,
      });

    try {
      await httpClient.post(endpoints.cart.add(), {
        is_solitaire: false,
        type: "product",
        identifier: productState.selectedPrice?.id,
      });
      if (window.Moengage) {
        window.Moengage.track_event("Added to Cart", {
          name: productState.data?.title,
          id: productState.data?.sku,
          amount: productState.selectedPrice?.discount
            ? productState.selectedPrice?.discount.total
            : productState.selectedPrice?.total,
        });
      }

      await dispatch(fetchCartItems());
      toast("Item added in cart", {
        type: "success",
        toastId: "cart-add-success",
      });
      fbqTrackEvent("AddToCart", {
        content_type: "product",
        content_ids: [productState.data?.sku],
      });
      ptrackEvent("addtocart", {
        event_id: `atc_${Date.now()}`,
        value: productState.selectedPrice?.discount
          ? productState.selectedPrice?.discount.total
          : productState.selectedPrice?.total,
        order_quantity: 1,
        currency: "INR",
      });
      if (
        productState.data?.categories &&
        productState.data?.categories.length
      ) {
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: "add_to_cart",
          ecommerce: {
            currency: "INR",
            value: productState.selectedPrice?.discount
              ? productState.selectedPrice?.discount.total
              : productState.selectedPrice?.total,
            items: [
              {
                item_id: productState.data?.sku,
                item_name: productState.data?.title,
                item_brand: GA4_ITEM_BRAND,
                item_category: [...productState.data?.categories].reverse()[0]
                  .name,
                item_variant: productState.selectedPrice?.purity,
                price: productState.selectedPrice?.discount
                  ? productState.selectedPrice?.discount.total
                  : productState.selectedPrice?.total,
                quantity: 1,
              },
            ],
          },
        });
      }
      syteTrackEvent({
        name: "products_added_to_cart",
        tag: "ecommerce",
        osh_id: authState.user?.uuid,
        products: [
          {
            sku: productState.data?.sku,
            price: productState.selectedPrice?.discount
              ? productState.selectedPrice?.discount.total
              : productState.selectedPrice?.total,
            quantity: 1,
          },
        ],
      });
    } catch (error: any) {
      if (Number(error?.response?.status) === 400) {
        toast("Something went wrong! Refreshing the page!", {
          type: "error",
          toastId: "refresh-error",
        });
        window.location.reload();
      }
    }
  };

  useEffect(() => {
    if (tryOnAddToCart) {
      setTryOnAddToCart(false);
      addToCart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tryOnAddToCart]);

  const removeFromCart = async () => {
    await httpClient.delete(endpoints.cart.remove(cartItem?.id));
    await dispatch(fetchCartItems());
    if (window.Moengage) {
      window.Moengage.track_event("Removed From Cart", {
        name: cartItem?.name,
        id: cartItem?.slug,
        amount: cartItem?.price,
      });
    }
    if (window.smartech) {
      unbxdCusEngTrackEvent("Remove from cart ", {
        source: "web",
        platform: "senco",
        prid: productState.data?.id,
        variant_id: productState.selectedPrice?.id,
        image: productState.data?.images[0].media,
        prqt: productState.data?.stock || 1,
        product_name: productState.data?.title,
        amount: productState.selectedPrice?.total,
        product_url: `${window.location.origin}/jewellery/${productState.data?.slug}`,
      });
    }
    toast("Item removed from cart", {
      type: "info",
      toastId: "cart-remove-success",
    });
    if (productState.data?.categories && productState.data?.categories.length) {
      window.gtag("event", "remove_from_cart", {
        currency: "INR",
        value: productState.selectedPrice?.discount
          ? productState.selectedPrice?.discount.total
          : productState.selectedPrice?.total,
        items: [
          {
            item_id: productState.data?.sku,
            item_name: productState.data?.title,
            item_category: [...productState.data?.categories].reverse()[0].name,
            price: productState.selectedPrice?.discount
              ? productState.selectedPrice?.discount.total
              : productState.selectedPrice?.total,
            quantity: 1,
          },
        ],
      });
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "remove_from_cart",
        ecommerce: {
          currency: "INR",
          value: productState.selectedPrice?.discount
            ? productState.selectedPrice?.discount.total
            : productState.selectedPrice?.total,
          items: [
            {
              item_id: productState.data?.sku,
              item_name: productState.data?.title,
              item_category: [...productState.data?.categories].reverse()[0]
                .name,
              price: productState.selectedPrice?.discount
                ? productState.selectedPrice?.discount.total
                : productState.selectedPrice?.total,
              quantity: 1,
            },
          ],
        },
      });
    }
  };

  useEffect(() => {
    if (tryOnRemoveFromCart) {
      setTryOnRemoveFromCart(false);
      removeFromCart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tryOnRemoveFromCart]);

  const openTryOn = () => {
    if (
      window.initMirrarUI &&
      productState.data &&
      process.env.NEXT_PUBLIC_STYLEDOTME_BRAND_ID
    ) {
      initTryOnEventListener((name, data) => {
        if (name == "add-to-cart") {
          if (data.productCode == productState.data?.sku) {
            setTryOnAddToCart(true);
          }
        }
        if (name == "ProductRemoveFromCart") {
          if (data.productCode == productState.data?.sku) {
            setTryOnRemoveFromCart(true);
          }
        }
      });
      window.initMirrarUI(productState.data.sku, {
        brandId: process.env.NEXT_PUBLIC_STYLEDOTME_BRAND_ID,
      });
      if (productState.data.categories && productState.data.categories.length) {
        window.gtag("event", "tryon-button-click", {
          event_category: "mirrar-internal-event",
          event_label: `${
            [...productState.data.categories].reverse()[0].name
          }/${productState.data.sku}/${
            productState.selectedPrice?.discount_total
          }`,
        });
      }
    }
  };

  if (!productState.data) {
    return <></>;
  }

  return (
    <>
      <div className="product_slider_zoom_slide">
        <div className={styles.product_slider}>
          <div className={styles.try_container}>
            <button
              role="button"
              className={`${styles.try__on} syte-discovery`}
              data-placement="PDP-button"
              onClick={() =>
                handleShow(productState.data?.medias[0]?.media_url)
              }
            >
              {similarLoading ? (
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <Spinner animation="border" size="sm" />
                  <span className={styles.v_t}>Loading..</span>
                </div>
              ) : (
                <>
                  <Image
                    src={require("@/assets/images/simi.svg")}
                    alt="Similar"
                  />
                  <span className={styles.v_t}>Similar</span>
                </>
              )}
            </button>
            {/* {!!productState.data.try_online && (
              <button
                role="button"
                aria-label="Try On"
                className={styles.try__on}
                onClick={openTryOn}
              >
                <Image
                  src={require("@/assets/images/tryon.svg")}
                  alt="Try On"
                />
                <span className={styles.v_t}>TryOn</span>
              </button>
            )} */}
          </div>
          {/* <div className={styles.best_seller}>BESTSELLER</div> */}
          {/* <div className={styles.life_time}>
            <span>100% CERTIFIED</span> • LIFETIME EXCHANGE @STORE
          </div> */}
          {width > 991 ? (
            <>
              <div
                className={
                  productState.data.images.length > 1 ? "" : "arrow_handle"
                }
              >
                <SliderImage
                  data={[
                    ...productState.data.medias.map((item) => ({
                      ...item,
                      preview:
                        item.media_type == "video"
                          ? "https://sencowebfiles.s3.ap-south-1.amazonaws.com/website/files/video-play-icon.png"
                          : "",
                    })),
                  ].map((v) => ({
                    image: v.media_url,
                    alt: productState.data?.title || "",
                    type: v.media_type,
                    preview: v.preview,
                  }))}
                  showDescription={false}
                  direction="right"
                />
              </div>
            </>
          ) : (
            <>
              <Swiper
                navigation={true}
                thumbs={{
                  swiper:
                    thumbsSwiper && !thumbsSwiper.destroyed
                      ? thumbsSwiper
                      : null,
                }}
                allowTouchMove={false}
                className="mySwiper2"
              >
                {[
                  ...productState.data.medias.map((item) => ({
                    ...item,
                    preview:
                      item.media_type == "video"
                        ? "https://sencowebfiles.s3.ap-south-1.amazonaws.com/website/files/video-play-icon.png"
                        : "",
                  })),
                ].map((v, k) => (
                  <SwiperSlide key={k}>
                    {v.media_type == "image" ? (
                      <InnerImageZoom
                        src={S3ImageLoader({ src: v.media_url, width: 800 })}
                        zoomSrc={v.media_url}
                        zoomScale={0.5}
                        imgAttributes={{
                          alt: `${productState.data?.title} - Senco Gold and Diamonds`,
                        }}
                      />
                    ) : (
                      <>
                        <div className={styles.video_middle}>
                          <video
                            ref={(el) => (videoRefs.current[k] = el)}
                            src={v.media_url}
                            controls
                            autoPlay
                            muted
                            playsInline
                            loop
                            style={{
                              maxWidth: "100%",
                              height: "auto",
                              opacity: videoLoading ? 0.5 : 1,
                            }}
                            onCanPlayThrough={(e) => {
                              (
                                e.currentTarget as HTMLVideoElement
                              ).style.background = "none";
                            }}
                            onError={(e) => {
                              (e.currentTarget as HTMLVideoElement).poster =
                                "/fallback.jpg"; // optional
                            }}
                            onCanPlay={() => setVideoLoading(false)}
                          />
                        </div>
                      </>
                    )}
                  </SwiperSlide>
                ))}
              </Swiper>

              <Swiper
                onSwiper={setThumbsSwiper as any}
                slidesPerView={4}
                spaceBetween={10}
                watchSlidesProgress={true}
                className={styles.mySwiper}
              >
                {[
                  ...productState.data.medias.map((item) => ({
                    ...item,
                    preview:
                      item.media_type == "video"
                        ? "https://sencowebfiles.s3.ap-south-1.amazonaws.com/website/files/video-play-icon.png"
                        : "",
                  })),
                ].map((v, k) => (
                  <SwiperSlide key={k} className={`${styles.swp} ${"swp-img"}`}>
                    <div className={styles.image_container_det}>
                      <Image
                        src={v.media_type == "video" ? v.preview : v.media_url}
                        alt={`${productState.data?.title} - Senco Gold and Diamonds`}
                        unoptimized
                        width={2500}
                        height={2500}
                        sizes="(max-width: 479px) 84px, (max-width: 640px) 129px, (max-width: 991px) 318px"
                        loader={S3ImageLoader}
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </>
          )}
        </div>
      </div>
      <SimilarProduct
        show={show}
        handleClose={handleClose}
        setIsOpen={setIsOpen}
        isOpen={isOpen}
        image={productState.data?.medias[0]?.media_url}
        skus={skus}
        setskus={setskus}
      />
    </>
  );
};

export default ProductDetailsSlider;
